import { useEffect, useState } from "react";
import { apiFetch } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Lockup } from "../components/Lockup";
import { PasswordField } from "../components/PasswordField";

// The MEMBER sign-in — an application surface on the desk's token system, LIGHT ONLY (v4 Part 2,
// founder ruling 27 Sep): there is no preference, no toggle and no OS path left to read, so nothing
// can flash dark at the door. Its own route and handler, never shared with staff (ruling §1 — the
// two failure models do not share a screen, a handler or a session shape); it is styled to look
// exactly like the homepage's modal. Design and copy are taken from that modal; nothing is improvised.
// Code-first with password fallback (M2), and it stays complete when SMS cannot send.

function deviceId() {
  let id = localStorage.getItem("juno_device_id");
  if (!id) { id = crypto.randomUUID(); localStorage.setItem("juno_device_id", id); }
  return id;
}

export default function MemberLogin() {
  const { setSession, reason, clearReason } = useAuth();
  const [caps, setCaps] = useState(null);
  const [step, setStep] = useState("identifier"); // identifier | code | password | newdevice | forgot
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [pwHide, setPwHide] = useState(0);   // bumped on submit: the field goes back to hidden
  const [code, setCode] = useState("");
  const [challengeId, setChallengeId] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [recoverySent, setRecoverySent] = useState(false);
  const [errKind, setErrKind] = useState("user");

  useEffect(() => { apiFetch("/identity/capabilities").then(setCaps).catch(() => setCaps({ sms_available: false })); }, []);
  // F59 (founder 27 Sep): the email CTA's ?b=<building> deep link must survive sign-in, however many
  // steps it takes (code, password, a new-device challenge). The URL never actually changes across
  // those steps, but stash it in sessionStorage too — belt-and-suspenders against any redirect that
  // drops the query string — so OwnerDesk has a second place to look once a session exists.
  useEffect(() => {
    const b = new URLSearchParams(window.location.search).get("b");
    if (b) sessionStorage.setItem("juno_pending_b", b);
  }, []);

  const finish = (d) => setSession({ access: d.access, sid: d.sid, account_id: d.account_id });

  // forceErr: show the fixed message regardless of the server detail — for the password step, the
  // message must be identical whether or not the account exists (no enumeration). clearReason() drops
  // the "You signed out." notice on the first submit (F13).
  // forceErr: show the fixed message regardless of the server detail — for the password step, the
  // message must be identical whether or not the account exists (no enumeration). kind drives the
  // styling: "system" (send/infra failure) is red, "user" (wrong password/code) is neutral ink.
  // clearReason() drops the "You signed out." notice on the first submit (F13).
  const call = async (path, body, onOk, fallbackErr, opts = {}) => {
    const { forceErr = false, kind = "user" } = opts;
    setErr(""); setErrKind(kind); clearReason(); setBusy(true);
    try { onOk(await apiFetch(path, { method: "POST", body })); }
    catch (e) { setErrKind(kind); setErr(forceErr ? fallbackErr : (e.detail || fallbackErr)); }
    finally { setBusy(false); }
  };

  const sendCode = () => call("/identity/login/start", { identifier },
    () => setStep("code"), "Could not send a code.", { kind: "system" });
  const submitCode = () => call("/identity/login/code",
    { identifier, challenge_id: challengeId, code, device_id: deviceId() }, finish, "That code did not work.");
  const submitPassword = () => { setPwHide((n) => n + 1); return call("/identity/login/password",
    { identifier, password, device_id: deviceId() },
    (d) => { if (d.status === "new_device") { setChallengeId(d.challenge_id); setStep("newdevice"); } else finish(d); },
    "That email and password don't match. Try again or reset your password.", { forceErr: true, kind: "user" }); };
  const submitNewDevice = () => call("/identity/new-device/verify",
    { identifier, challenge_id: challengeId, code, device_id: deviceId() }, finish, "That code did not work.");
  const sendRecovery = () => call("/identity/recovery/start", { email: identifier },
    () => setRecoverySent(true), "Could not send a reset link — try again shortly.", { kind: "system" });
  const goForgot = () => { setErr(""); clearReason(); setRecoverySent(false); setStep("forgot"); };

  const onEnter = (fn) => (e) => { if (e.key === "Enter") fn(); };

  const AlertGlyph = () => (
    <svg className="mlog-err-glyph" width="14" height="14" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" /><line x1="12" y1="8" x2="12" y2="13" /><line x1="12" y1="16.5" x2="12" y2="16.5" />
    </svg>
  );
  const ErrLine = () => (err ? (
    <div className={`mlog-err mlog-err-${errKind}`} role="alert" data-testid="login-error">
      <AlertGlyph /><span>{err}</span>
    </div>
  ) : null);

  return (
    <div className="mlog-wrap">
      <div className="mlog-card" data-testid="member-login">
        <div className="mlog-brand"><Lockup size="pre-account" /></div>
        <div className="mlog-eyebrow">OWNER DESK · SIGN IN</div>

        {reason && <div className="mlog-banner" data-testid="login-reason" onClick={clearReason}>{reason}</div>}

        {step === "identifier" && (
          <div className="mlog-step">
            <label className="mlog-label">Email</label>
            <input className="mlog-input" data-testid="login-identifier" value={identifier} autoFocus
                   onChange={(e) => setIdentifier(e.target.value)}
                   onKeyDown={onEnter(caps && caps.sms_available ? sendCode : () => setStep("password"))} />
            {caps && caps.sms_available ? (
              <>
                <p className="mlog-helper">We text a six-digit code. No password to remember, and the
                  code only reaches the device on the account.</p>
                <button className="mlog-primary" data-testid="login-send-code" disabled={busy || !identifier}
                        onClick={sendCode}>Send me a code</button>
                <button className="mlog-link" data-testid="login-use-password"
                        onClick={() => setStep("password")}>Use a password instead</button>
              </>
            ) : (
              <>
                {caps && caps.sms_note && <p className="mlog-helper" data-testid="sms-note">{caps.sms_note}</p>}
                <button className="mlog-primary" data-testid="login-continue-password" disabled={!identifier}
                        onClick={() => setStep("password")}>Continue with password</button>
              </>
            )}
            <ErrLine />
          </div>
        )}

        {step === "code" && (
          <div className="mlog-step">
            <div className="mlog-substate">SIX-DIGIT CODE SENT</div>
            <p className="mlog-helper">Six digits sent to {identifier}.</p>
            <label className="mlog-label">Six-digit code</label>
            <input className="mlog-input" data-testid="login-code" value={code} autoFocus inputMode="numeric"
                   onChange={(e) => setCode(e.target.value)} onKeyDown={onEnter(submitCode)} />
            <button className="mlog-primary" data-testid="login-code-submit" disabled={busy || !code}
                    onClick={submitCode}>Sign in</button>
            <ErrLine />
            <button className="mlog-link" onClick={() => { setStep("identifier"); setCode(""); }}>Use a different email or mobile</button>
            <button className="mlog-link" data-testid="login-use-password"
                    onClick={() => { setStep("password"); setCode(""); }}>Use a password instead</button>
          </div>
        )}

        {step === "password" && (
          <div className="mlog-step">
            <label className="mlog-label">Email</label>
            <input className="mlog-input" data-testid="login-identifier-pw" value={identifier}
                   onChange={(e) => setIdentifier(e.target.value)} />
            <label className="mlog-label">Password</label>
            <PasswordField className="mlog-input" testid="login-password" value={password} autoFocus
                   hideOn={pwHide}
                   onChange={(e) => setPassword(e.target.value)} onKeyDown={onEnter(submitPassword)} />
            <button className="mlog-primary" data-testid="login-password-submit" disabled={busy || !identifier || !password}
                    onClick={submitPassword}>Sign in</button>
            <ErrLine />
            <button className="mlog-link" data-testid="login-forgot" onClick={goForgot}>Forgot your password?</button>
            {caps && caps.sms_available && (
              <button className="mlog-link" onClick={() => { setStep("identifier"); setPassword(""); }}>
                Send me a code instead
              </button>
            )}
          </div>
        )}

        {step === "forgot" && (
          <div className="mlog-step">
            <div className="mlog-substate mlog-substate-neutral">RESET YOUR PASSWORD</div>
            {recoverySent ? (
              <p className="mlog-helper" data-testid="login-recovery-sent">If there's an account for that
                email, a reset link is on its way. It works once and expires in an hour.</p>
            ) : (
              <>
                <label className="mlog-label">Email</label>
                <input className="mlog-input" data-testid="login-recovery-email" value={identifier} autoFocus
                       onChange={(e) => setIdentifier(e.target.value)} onKeyDown={onEnter(sendRecovery)} />
                <p className="mlog-helper">We'll email you a link to set a new password.</p>
                <button className="mlog-primary" data-testid="login-recovery-send" disabled={busy || !identifier}
                        onClick={sendRecovery}>Send reset link</button>
                <ErrLine />
              </>
            )}
            <button className="mlog-link" data-testid="login-recovery-back"
                    onClick={() => { setErr(""); setStep("password"); }}>Back to sign in</button>
          </div>
        )}

        {step === "newdevice" && (
          <div className="mlog-step">
            <div className="mlog-substate">NEW DEVICE · CODE SENT</div>
            <p className="mlog-helper">Six digits sent to {identifier}. A new device always asks for a
              code, even when you have signed in before.</p>
            <label className="mlog-label">Six-digit code</label>
            <input className="mlog-input" data-testid="login-newdevice-code" value={code} autoFocus inputMode="numeric"
                   onChange={(e) => setCode(e.target.value)} onKeyDown={onEnter(submitNewDevice)} />
            <button className="mlog-primary" data-testid="login-newdevice-submit" disabled={busy || !code}
                    onClick={submitNewDevice}>Confirm and sign in</button>
            <ErrLine />
          </div>
        )}

        <div className="mlog-note">One session at a time. Signing in here ends your session on any other device.</div>
      </div>
    </div>
  );
}
